import React, { useEffect, useMemo, useState } from "react";
import { CrewPilotModuleShell } from "./CrewPilotModuleShell";
import { PAGE_LAYOUT } from "../layout/pageLayout";
import { CPInput } from "./CPInput";
import {
  CP_MODAL_OVERLAY_STYLE,
  CP_MODAL_PANEL_STYLE,
} from "./shared/crewpilotModalStyles";
import { ViewportPortal } from "./shared/ViewportDialog";
import { CPDropdown } from "./CPDropdown";
import {
  ESTIMATE_USER_EDITABLE_STATUS_OPTIONS,
  formatEstimateStatus,
  formatEstimateUpdatedAt,
  resolveEstimateStatus,
} from "../helpers/estimates";
import { getEstimateSummaryActions } from "../helpers/estimateWorkflow";
import {
  computeEstimateTotals,
  formatCurrency,
} from "../helpers/estimateTotals";
import {
  applyTaxTypeSelection,
  computeEstimateTaxSummary,
  ESTIMATE_TAX_TYPE_OPTIONS,
  formatStandardTaxRateDisplay,
  getTaxableSubtotal,
  parseEstimateTaxLineFromTree,
  taxLineToFormState,
} from "../helpers/estimateTax";
import { EstimateOverflowMenu } from "./estimate/EstimateOverflowMenu";
import { useIsDesktopViewport } from "../hooks/useIsDesktopViewport";
import { EstimateSummaryDesktop } from "./estimate/desktop/EstimateSummaryDesktop";
import "./estimate/estimateWorkspace.css";

const SECTION_STYLE = {
  marginTop: "24px",
  paddingTop: "16px",
  borderTop: "1px solid rgba(201,168,76,0.2)",
};

const MODAL_OVERLAY_STYLE = CP_MODAL_OVERLAY_STYLE;
const MODAL_PANEL_STYLE = CP_MODAL_PANEL_STYLE;

/**
 * Compact Tax Line row: type + rate side by side instead of a bordered
 * "Tax Line N" card. Same fields, same applyTaxTypeSelection/onChange
 * contract, same custom-name/rate path — presentation only.
 */
function TaxLineEditor({ lineNumber, line, onChange, disabled }) {
  function update(patch) {
    onChange({ ...line, ...patch });
  }

  function handleTypeChange(nextType) {
    onChange(applyTaxTypeSelection(line, nextType));
  }

  return (
    <div className="est-tax-line">
      <div className="est-section-summary-label" style={{ marginBottom: 6 }}>
        Tax {lineNumber}
      </div>

      <div className="est-tax-row">
        <CPDropdown
          label="Tax Type"
          value={line.type}
          onChange={handleTypeChange}
          options={ESTIMATE_TAX_TYPE_OPTIONS}
          placeholder="Select tax type"
          disabled={disabled}
        />

        {line.type !== "custom" ? (
          <CPInput
            label="Rate %"
            value={formatStandardTaxRateDisplay(line.type)}
            readOnly
            disabled
          />
        ) : (
          <CPInput
            label="Rate %"
            value={line.rate}
            onChange={(value) => update({ rate: value })}
            type="number"
            placeholder="0"
            disabled={disabled}
          />
        )}
      </div>

      {line.type === "custom" && (
        <CPInput
          label="Tax Name"
          value={line.customName}
          onChange={(value) => update({ customName: value })}
          placeholder="e.g. Municipal Tax"
          disabled={disabled}
        />
      )}
    </div>
  );
}

function SummaryLine({ label, value, emphasis = false, indent = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: emphasis ? "10px" : "8px",
        marginLeft: indent ? "16px" : 0,
        fontSize: emphasis ? "15px" : "14px",
        fontWeight: emphasis ? 700 : 500,
        color: emphasis ? "#c9a84c" : "inherit",
        opacity: indent ? 0.9 : 1,
      }}
    >
      <span>{label}</span>
      <span>{formatCurrency(value)}</span>
    </div>
  );
}

export function EstimateSummaryScreen({
  estimateTree,
  materialsCatalog,
  editable,
  saving,
  pricingSchemaMissing = false,
  linkedProjectId = null,
  onBack,
  onEditEstimate,
  onReviewEstimate,
  onUpdateEstimate,
  onCreateProject,
  onOpenProject,
  onSavePricing,
  onStatusChange,
  onArchiveEstimate,
  onGenerateReport,
  onPreviewReport,
}) {
  const isDesktop = useIsDesktopViewport();
  const financials = useMemo(
    () => computeEstimateTotals(estimateTree, materialsCatalog),
    [estimateTree, materialsCatalog],
  );

  const [status, setStatus] = useState(() =>
    resolveEstimateStatus(estimateTree?.status),
  );
  const [wasteAdjustment, setWasteAdjustment] = useState("");
  const [travelAdjustment, setTravelAdjustment] = useState("");
  const [miscAdjustment, setMiscAdjustment] = useState("");
  const [finalEstimatePrice, setFinalEstimatePrice] = useState("");
  const [taxLine1, setTaxLine1] = useState(() => taxLineToFormState());
  const [taxLine2, setTaxLine2] = useState(() => taxLineToFormState());
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);

  useEffect(() => {
    setStatus(resolveEstimateStatus(estimateTree?.status));
  }, [estimateTree?.status]);

  useEffect(() => {
    setWasteAdjustment(
      estimateTree?.waste_adjustment != null &&
        Number(estimateTree.waste_adjustment) !== 0
        ? String(estimateTree.waste_adjustment)
        : "",
    );
    setTravelAdjustment(
      estimateTree?.travel_adjustment != null &&
        Number(estimateTree.travel_adjustment) !== 0
        ? String(estimateTree.travel_adjustment)
        : "",
    );
    setMiscAdjustment(
      estimateTree?.misc_adjustment != null &&
        Number(estimateTree.misc_adjustment) !== 0
        ? String(estimateTree.misc_adjustment)
        : "",
    );
    setFinalEstimatePrice(
      estimateTree?.final_estimate_price != null &&
        estimateTree.final_estimate_price !== ""
        ? String(estimateTree.final_estimate_price)
        : "",
    );
    setTaxLine1(taxLineToFormState(parseEstimateTaxLineFromTree(estimateTree, 1)));
    setTaxLine2(taxLineToFormState(parseEstimateTaxLineFromTree(estimateTree, 2)));
  }, [estimateTree]);

  const liveAdjustedTotal = useMemo(() => {
    const waste = Number(wasteAdjustment) || 0;
    const travel = Number(travelAdjustment) || 0;
    const misc = Number(miscAdjustment) || 0;
    return financials.calculatedEstimateTotal + waste + travel + misc;
  }, [
    financials.calculatedEstimateTotal,
    wasteAdjustment,
    travelAdjustment,
    miscAdjustment,
  ]);

  const taxableSubtotal = useMemo(
    () =>
      getTaxableSubtotal(financials, {
        liveFinalEstimatePrice: finalEstimatePrice,
      }),
    [financials, finalEstimatePrice],
  );

  const taxSummary = useMemo(
    () => computeEstimateTaxSummary(taxableSubtotal, [taxLine1, taxLine2]),
    [taxableSubtotal, taxLine1, taxLine2],
  );

  async function handleSavePricing() {
    if (!editable) return;

    await onSavePricing?.({
      wasteAdjustment,
      travelAdjustment,
      miscAdjustment,
      finalEstimatePrice,
      taxLines: [taxLine1, taxLine2],
    });
  }

  async function handleStatusChange(nextStatus) {
    if (!nextStatus || nextStatus === status) return;
    if (nextStatus === "converted" || nextStatus === "archived") return;

    const previousStatus = status;
    setStatus(nextStatus);

    try {
      await onStatusChange?.(nextStatus);
    } catch (error) {
      setStatus(previousStatus);
      console.error("[EstimateSummary] Status update failed:", error);
    }
  }

  function handlePreviewReport() {
    onPreviewReport?.();
  }

  function handleGenerateReport() {
    onGenerateReport?.();
  }

  async function handleConfirmArchive() {
    if (archiving || saving) return;

    setArchiving(true);
    try {
      await onArchiveEstimate?.();
      setStatus("archived");
      setShowArchiveConfirm(false);
    } catch (error) {
      console.error("[EstimateSummary] Archive failed:", error);
    } finally {
      setArchiving(false);
    }
  }

  const summaryActions = getEstimateSummaryActions(status, { linkedProjectId });
  const isSystemManagedStatus =
    status === "converted" || status === "archived";

  const clientName =
    estimateTree?.clients?.client_name ||
    estimateTree?.client_name_snapshot ||
    "—";

  // Job-Wide/General is an implementation-detail default scope. Hide it from
  // the Summary only when it is genuinely empty (no Features and $0 total) —
  // the underlying record, its creation, and its deletion-protection are
  // untouched (same presentation-only rule established in the Estimate
  // Workspace redesign). If it carries real work/value, it is shown like any
  // other location.
  const visibleLocations = financials.locations.filter(
    (location) =>
      !(
        location.isDefaultScope &&
        location.workTypes.length === 0 &&
        location.locationTotal === 0
      ),
  );

  const overflowMenuItems = [
    status !== "archived" && {
      label: "Archive Estimate",
      onClick: () => setShowArchiveConfirm(true),
      destructive: true,
      disabled: saving || archiving,
    },
  ];

  const archiveModal = showArchiveConfirm ? (
        <ViewportPortal>
        <div
          style={MODAL_OVERLAY_STYLE}
          onClick={() => !archiving && setShowArchiveConfirm(false)}
        >
          <div
            style={MODAL_PANEL_STYLE}
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-estimate-title"
          >
            <h2
              id="archive-estimate-title"
              style={{ marginTop: 0, marginBottom: "12px", fontSize: "20px" }}
            >
              Archive Estimate?
            </h2>
            <p style={{ opacity: 0.85, lineHeight: 1.5, marginBottom: "20px" }}>
              This estimate will be removed from active estimate lists and moved
              to Archived estimates.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                type="button"
                className="nx-btn"
                onClick={() => setShowArchiveConfirm(false)}
                disabled={archiving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="nx-btn nx-btn-gold"
                onClick={handleConfirmArchive}
                disabled={archiving}
              >
                {archiving ? "Archiving…" : "Archive"}
              </button>
            </div>
          </div>
        </div>
        </ViewportPortal>
      ) : null;

  if (isDesktop) {
    const disabledPricing = !editable || pricingSchemaMissing;
    const actionButtons = [
      summaryActions.editEstimate && ["edit", "Edit Estimate", onEditEstimate],
      summaryActions.previewReport && ["preview", "Preview Report", handlePreviewReport],
      summaryActions.reviewEstimate && ["review", "Review Estimate", onReviewEstimate],
      summaryActions.generateReport && ["generate", "Generate Report", handleGenerateReport],
      summaryActions.updateEstimate && ["update", "Update Estimate", onUpdateEstimate],
      summaryActions.createProject && ["create", "Create Project", onCreateProject, true],
      summaryActions.openProject && ["open", "Open Project", onOpenProject, true],
    ].filter(Boolean);

    return (
      <EstimateSummaryDesktop
        title={estimateTree?.title || "Untitled Estimate"}
        clientName={clientName}
        estimateNumber={estimateTree?.estimate_number}
        status={status}
        updatedLabel={formatEstimateUpdatedAt(estimateTree?.updated_at)}
        onBack={onBack}
        statusControl={
          isSystemManagedStatus ? null : (
            <CPDropdown
              label="Status"
              value={status}
              onChange={handleStatusChange}
              options={ESTIMATE_USER_EDITABLE_STATUS_OPTIONS}
              placeholder="Select status"
              disabled={saving || archiving}
            />
          )
        }
        actions={actionButtons.map(([key, label, onClick, primary]) => (
          <button
            key={key}
            type="button"
            className={`dt-btn${primary ? " dt-btn--primary" : ""}`}
            onClick={onClick}
            disabled={primary ? saving : undefined}
          >
            {label}
          </button>
        ))}
        archiveItem={
          status !== "archived" && !(saving || archiving)
            ? { key: "archive", label: "Archive Estimate", danger: true }
            : null
        }
        onArchive={() => setShowArchiveConfirm(true)}
        financials={financials}
        taxSummary={taxSummary}
        liveAdjustedTotal={liveAdjustedTotal}
        visibleLocations={visibleLocations}
        pricingSchemaMissing={pricingSchemaMissing}
        canSavePricing={editable && !pricingSchemaMissing}
        saving={saving}
        onSavePricing={handleSavePricing}
        adjustmentsNode={
          <div className="est-d-grid-3">
            <CPInput label="Waste ($)" value={wasteAdjustment} onChange={setWasteAdjustment} type="number" placeholder="0" disabled={disabledPricing} />
            <CPInput label="Travel ($)" value={travelAdjustment} onChange={setTravelAdjustment} type="number" placeholder="0" disabled={disabledPricing} />
            <CPInput label="Misc ($)" value={miscAdjustment} onChange={setMiscAdjustment} type="number" placeholder="0" disabled={disabledPricing} />
          </div>
        }
        finalPriceNode={
          <div className="est-d-grid-3">
            <CPInput
              value={finalEstimatePrice}
              onChange={setFinalEstimatePrice}
              type="number"
              placeholder={liveAdjustedTotal > 0 ? `e.g. ${liveAdjustedTotal.toFixed(2)}` : "Manual override"}
              disabled={disabledPricing}
              aria-label="Final Estimate Price"
            />
          </div>
        }
        taxNode={
          <div className="est-d-grid-2">
            <TaxLineEditor lineNumber={1} line={taxLine1} onChange={setTaxLine1} disabled={disabledPricing} />
            <TaxLineEditor lineNumber={2} line={taxLine2} onChange={setTaxLine2} disabled={disabledPricing} />
          </div>
        }
      >
        {archiveModal}
      </EstimateSummaryDesktop>
    );
  }

  return (
    <CrewPilotModuleShell
      title={estimateTree?.title || "Untitled Estimate"}
      subtitle={clientName}
      sectionLabel="Estimate"
      onBack={onBack}
      layout={PAGE_LAYOUT.HUB}
    >
      <div className="est-summary-meta-row">
        <span>{estimateTree?.estimate_number}</span>
        <span className="est-page-subtitle-dot">·</span>
        <span className="est-status-pill">{formatEstimateStatus(status)}</span>
      </div>
      <div className="est-summary-updated">
        Last Updated: {formatEstimateUpdatedAt(estimateTree?.updated_at)}
      </div>

      {!isSystemManagedStatus && (
        <div style={{ marginBottom: "16px" }}>
          <CPDropdown
            label="Status"
            value={status}
            onChange={handleStatusChange}
            options={ESTIMATE_USER_EDITABLE_STATUS_OPTIONS}
            placeholder="Select status"
            disabled={saving || archiving}
          />
        </div>
      )}

      <div className="est-action-row">
        {summaryActions.editEstimate && (
          <button type="button" className="est-action-btn" onClick={onEditEstimate}>
            Edit Estimate
          </button>
        )}
        {summaryActions.previewReport && (
          <button
            type="button"
            className="est-action-btn"
            onClick={handlePreviewReport}
          >
            Preview Report
          </button>
        )}
        {summaryActions.reviewEstimate && (
          <button type="button" className="est-action-btn" onClick={onReviewEstimate}>
            Review Estimate
          </button>
        )}
        {summaryActions.generateReport && (
          <button
            type="button"
            className="est-action-btn"
            onClick={handleGenerateReport}
          >
            Generate Report
          </button>
        )}
        {summaryActions.updateEstimate && (
          <button type="button" className="est-action-btn" onClick={onUpdateEstimate}>
            Update Estimate
          </button>
        )}
        {summaryActions.createProject && (
          <button
            type="button"
            className="est-action-btn est-action-btn--gold"
            onClick={onCreateProject}
            disabled={saving}
          >
            Create Project
          </button>
        )}
        {summaryActions.openProject && (
          <button
            type="button"
            className="est-action-btn est-action-btn--gold"
            onClick={onOpenProject}
            disabled={saving}
          >
            Open Project
          </button>
        )}
        <EstimateOverflowMenu items={overflowMenuItems} ariaLabel="Estimate actions" />
      </div>

      <div style={SECTION_STYLE}>
        <div className="est-section-summary-label" style={{ marginBottom: "12px" }}>
          Financial Summary
        </div>
        <SummaryLine
          label="Calculated Materials Total"
          value={financials.calculatedMaterialsTotal}
        />
        <SummaryLine
          label="Calculated Labour Total"
          value={financials.calculatedLabourTotal}
        />
        <SummaryLine
          label="Calculated Estimate Total"
          value={financials.calculatedEstimateTotal}
          emphasis
        />
      </div>

      <div style={SECTION_STYLE}>
        <div className="est-section-summary-label" style={{ marginBottom: "12px" }}>
          Project Adjustments
        </div>

        {pricingSchemaMissing && (
          <div
            style={{
              marginBottom: "12px",
              padding: "10px 12px",
              borderRadius: "10px",
              border: "1px solid rgba(201,168,76,0.45)",
              background: "rgba(201,168,76,0.08)",
              color: "#c9a84c",
              fontSize: "13px",
            }}
          >
            Pricing fields are not applied yet. Run migrations{" "}
            <code>20260625120000_estimate_pricing_summary.sql</code> and{" "}
            <code>20260629120000_estimate_tax_summary.sql</code> in Supabase.
          </div>
        )}

        <div className="est-adjustments-row">
          <CPInput
            label="Waste ($)"
            value={wasteAdjustment}
            onChange={setWasteAdjustment}
            type="number"
            placeholder="0"
            disabled={!editable || pricingSchemaMissing}
          />
          <CPInput
            label="Travel ($)"
            value={travelAdjustment}
            onChange={setTravelAdjustment}
            type="number"
            placeholder="0"
            disabled={!editable || pricingSchemaMissing}
          />
          <CPInput
            label="Misc ($)"
            value={miscAdjustment}
            onChange={setMiscAdjustment}
            type="number"
            placeholder="0"
            disabled={!editable || pricingSchemaMissing}
          />
        </div>

        <SummaryLine label="Adjusted Total" value={liveAdjustedTotal} emphasis />
      </div>

      <div style={SECTION_STYLE}>
        <div className="est-section-summary-label" style={{ marginBottom: "12px" }}>
          Final Price
        </div>
        <CPInput
          value={finalEstimatePrice}
          onChange={setFinalEstimatePrice}
          type="number"
          placeholder={
            liveAdjustedTotal > 0
              ? `e.g. ${liveAdjustedTotal.toFixed(2)}`
              : "Manual override"
          }
          disabled={!editable || pricingSchemaMissing}
          aria-label="Final Estimate Price"
        />
        {financials.finalEstimatePrice != null && (
          <SummaryLine
            label="Saved Final Estimate Price"
            value={financials.finalEstimatePrice}
            emphasis
          />
        )}
      </div>

      <div style={SECTION_STYLE}>
        <div className="est-section-summary-label" style={{ marginBottom: "12px" }}>
          Tax
        </div>

        <TaxLineEditor
          lineNumber={1}
          line={taxLine1}
          onChange={setTaxLine1}
          disabled={!editable || pricingSchemaMissing}
        />
        <TaxLineEditor
          lineNumber={2}
          line={taxLine2}
          onChange={setTaxLine2}
          disabled={!editable || pricingSchemaMissing}
        />

        <div style={{ marginTop: "8px" }}>
          <SummaryLine label="Subtotal" value={taxSummary.subtotal} />
          {taxSummary.lines.map((line) => (
            <SummaryLine
              key={`${line.type}-${line.label}-${line.rate}`}
              label={`${line.label} (${line.rate}%)`}
              value={line.amount}
              indent
            />
          ))}
          <SummaryLine
            label="Grand Total"
            value={taxSummary.grandTotal}
            emphasis
          />
        </div>

        {editable && !pricingSchemaMissing && (
          <button
            type="button"
            className="est-action-btn est-action-btn--gold"
            style={{ marginTop: "12px", width: "100%", textAlign: "center" }}
            onClick={handleSavePricing}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Pricing"}
          </button>
        )}
      </div>

      <div style={SECTION_STYLE}>
        <div className="est-section-summary-label" style={{ marginBottom: "12px" }}>
          Location Summary
        </div>

        {visibleLocations.length === 0 ? (
          <div className="est-home-empty-row">No locations yet.</div>
        ) : (
          visibleLocations.map((location) => (
            <div key={location.locationId} className="est-location-row">
              <div className="est-location-row-header">
                <span>{location.locationName}</span>
                <span>{formatCurrency(location.locationTotal)}</span>
              </div>
              {location.workTypes.map((wt) => (
                <SummaryLine
                  key={wt.workTypeId}
                  label={wt.label}
                  value={wt.workTypeTotal}
                  indent
                />
              ))}
            </div>
          ))
        )}
      </div>

      <div className="est-bottom-spacer" />

      {archiveModal}
    </CrewPilotModuleShell>
  );
}
